import { useState, useEffect } from 'react'
import { useImagePicker } from '../hooks/useImages'
import SharePointImagePicker from './SharePointImagePicker'
import { unifiedImageService, UnifiedImageFile } from '../utils/unifiedImageService'
import { sharepointServiceProduction } from '../utils/sharepointServiceProduction'
import { publicCacheService, PublicKit, PublicImage } from '../utils/publicCacheService'
import { Kit } from '../types/kit'

// Note: Using unified Kit type from ../types/kit.ts
// Old "Product" interface has been replaced with centralized Kit type
// Old "stockCount" property is now "stockCount"

function AdminProductsManager() {
  const [products, setProducts] = useState<Kit[]>([])
  const [editingProduct, setEditingProduct] = useState<Kit | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterStock, setFilterStock] = useState<string>('all')
  const [filterActive, setFilterActive] = useState<string>('all')
  const [kitImages, setKitImages] = useState<UnifiedImageFile[]>([])
  const { isPickerOpen, openPicker, closePicker } = useImagePicker()
  const [pickingImageFor, setPickingImageFor] = useState<string | null>(null)

  const categories = ['Sea Glass Kits', 'Watercolor Kits', 'Resin Kits', 'Mixed Media Kits', 'Starter Kits']

  // Function to load products from JSON files
  const loadProducts = async () => {
    try {
      const response = await fetch('/data/kits.json')
      if (response.ok) {
        const kitsData = await response.json()
        console.log('AdminProductsManager: Loaded kits from JSON:', kitsData.length)

        // Normalize data to ensure features and tags are arrays
        const normalizedProducts = kitsData.map((product: any) => ({
          ...product,
          features: Array.isArray(product.features) ? product.features : [],
          tags: Array.isArray(product.tags) ? product.tags : []
        }))

        setProducts(normalizedProducts)

        // Also store in localStorage for dashboard access
        try {
          localStorage.setItem('salty_mermaid_kits', JSON.stringify(normalizedProducts))
          console.log('AdminProductsManager: Stored kits in localStorage for dashboard')
          publicCacheService.notifyCacheUpdated()
        } catch (error) {
          console.error('AdminProductsManager: Error storing to localStorage:', error)
        }
      } else {
        console.log('AdminProductsManager: No kits.json found')
        setProducts([])
        localStorage.setItem('salty_mermaid_kits', JSON.stringify([]))
      }
    } catch (error) {
      console.error('AdminProductsManager: Error loading kits from JSON:', error)
      setProducts([])
      localStorage.setItem('salty_mermaid_kits', JSON.stringify([]))
    }
  }

  // Load products from JSON files
  useEffect(() => {
    loadProducts()

    // Listen for SharePoint data updates
    const handleSharePointUpdate = () => {
      console.log('AdminProductsManager: SharePoint data updated, reloading...')
      loadProducts()
    }

    window.addEventListener('sharepointDataLoaded', handleSharePointUpdate)

    return () => {
      window.removeEventListener('sharepointDataLoaded', handleSharePointUpdate)
    }
  }, [])

  // JSON-only mode - no localStorage saving needed

  // Load images from both sources
  useEffect(() => {
    const loadImages = async () => {
      const images = await unifiedImageService.getImagesByCategory('kits')
      setKitImages(images)
    }
    loadImages()
    
    // Subscribe to changes
    const unsubscribe = unifiedImageService.subscribe(() => {
      loadImages()
    })
    return unsubscribe
  }, [])

  // Update public cache whenever products OR images change
  useEffect(() => {
    console.log('AdminProducts: Updating public cache due to products or images change')
    updatePublicCache().catch(error => {
      console.error('AdminProducts: Error updating public cache:', error)
    })
  }, [products, kitImages])

  const handleImageSelect = async (image: SharePointImageFile) => {
    console.log('AdminProducts: Image selected:', image.id, 'for product:', pickingImageFor)
    if (pickingImageFor && editingProduct && pickingImageFor === editingProduct.id) {
      console.log('AdminProducts: Updating product imageId')
      const updatedProduct = { ...editingProduct, imageId: image.id }
      setEditingProduct(updatedProduct)

      // IMPORTANT: Immediately update the products list and write to JSON so public site sees the change
      const updatedProducts = products.map(p =>
        p.id === updatedProduct.id ? updatedProduct : p
      )
      setProducts(updatedProducts)

      try {
        console.log('Products: Storing updated products in localStorage after image assignment...')
        localStorage.setItem('salty_mermaid_kits', JSON.stringify(updatedProducts))
        console.log('Products: Successfully stored to localStorage after image assignment')
        publicCacheService.notifyCacheUpdated()
      } catch (error) {
        console.error('Products: Error storing to localStorage after image assignment:', error)
      }
    }
    setPickingImageFor(null)
    closePicker()
  }

  const openImagePicker = (productId: string) => {
    setPickingImageFor(productId)
    openPicker('kits')
  }

  const handleSaveProduct = async (product: Product) => {
    console.log('Products: Saving product:', product.name, 'with imageId:', product.imageId)

    const now = new Date().toISOString()
    let savedProduct: Product

    if (product.id === 'new') {
      savedProduct = {
        ...product,
        id: Date.now().toString(),
        createdAt: now,
        lastUpdated: now,
        stockLastUpdated: now
      }
      console.log('Products: Creating new product with imageId:', savedProduct.imageId)
      setProducts(prev => [...prev, savedProduct])
    } else {
      // Check if stock changed to update stockLastUpdated
      const existingProduct = products.find(p => p.id === product.id)
      const stockChanged = existingProduct && existingProduct.stockCount !== product.stockCount

      savedProduct = {
        ...product,
        lastUpdated: now,
        stockLastUpdated: stockChanged ? now : product.stockLastUpdated,
        createdAt: product.createdAt || now
      }
      console.log('Products: Updating existing product with imageId:', savedProduct.imageId)
      if (stockChanged) {
        console.log(`Products: Stock changed from ${existingProduct?.stockCount} to ${product.stockCount}, updating stockLastUpdated`)
      }
      setProducts(prev => prev.map(existing =>
        existing.id === savedProduct.id ? savedProduct : existing
      ))
    }
    setEditingProduct(null)
    setShowAddForm(false)

    // Store in localStorage for client-side persistence
    try {
      console.log('Products: Storing updated kits in localStorage...')
      const updatedProducts = product.id === 'new'
        ? [...products, savedProduct]
        : products.map(existing => existing.id === savedProduct.id ? savedProduct : existing)

      localStorage.setItem('salty_mermaid_kits', JSON.stringify(updatedProducts))
      console.log('Products: Successfully stored to localStorage')
      publicCacheService.notifyCacheUpdated()
    } catch (error) {
      console.error('Products: Error storing to localStorage:', error)
    }

    // Auto-sync individual item to SharePoint after save
    try {
      console.log('Products: Auto-syncing individual kit to SharePoint...')
      const syncResult = await sharepointServiceProduction.syncKitItem(savedProduct)
      if (syncResult.success) {
        console.log('Products: Successfully synced kit to SharePoint:', syncResult.message)
        // Update public cache after successful individual sync
        await updatePublicCache()
      } else {
        console.warn('Products: SharePoint sync failed for kit:', syncResult.message)
      }
    } catch (error) {
      console.error('Products: SharePoint sync error for kit:', error)
    }
  }

  const handleDeleteProduct = async (productId: string) => {
    const product = products.find(p => p.id === productId)
    if (confirm(`Are you sure you want to delete "${product?.name}"?`)) {
      setProducts(prev => prev.filter(p => p.id !== productId))
      
      // Auto-sync individual item deletion to SharePoint
      try {
        console.log('Products: Auto-syncing individual kit deletion to SharePoint...')
        const syncResult = await sharepointServiceProduction.deleteKitItem(productId)
        if (syncResult.success) {
          console.log('Products: Successfully deleted kit from SharePoint:', syncResult.message)
          // Update public cache after successful individual deletion
          await updatePublicCache()
        } else {
          console.warn('Products: SharePoint deletion failed for kit:', syncResult.message)
        }
      } catch (error) {
        console.error('Products: SharePoint deletion error for kit:', error)
      }
    }
  }

  const handleToggleActive = (productId: string) => {
    setProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, isActive: !p.isActive } : p
    ))
  }

  const handleStockUpdate = (productId: string, newStock: number) => {
    setProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, stockCount: newStock, inStock: newStock > 0 } : p
    ))
  }

  const getProductImage = (product: Product): UnifiedImageFile | null => {
    if (!product.imageId) return null
    return kitImages.find(img => img.id === product.imageId) || null
  }

  // Update public cache with current admin data
  const updatePublicCache = async () => {
    console.log('AdminProducts: Updating public cache...')
    
    // Don't update cache if data isn't ready
    if (!products || products.length === 0) {
      console.log('AdminProducts: Skipping cache update - no products data')
      return
    }
    
    if (!Array.isArray(kitImages)) {
      console.log('AdminProducts: Skipping cache update - images not loaded yet')
      return
    }
    
    // Convert admin products to public kit format
    const publicKits: PublicKit[] = products.map(product => ({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      category: product.category,
      inStock: product.inStock,
      stockCount: product.stockCount,
      imageId: product.imageId,
      isActive: product.isActive,
      materials: product.features || [], // Use features as materials with fallback
      difficulty: (product.tags || []).find(tag => ['Beginner', 'Intermediate', 'Advanced'].includes(tag))
    }))

    // Convert admin images to public format (only kit images)
    const publicKitImages: PublicImage[] = kitImages.map(img => {
      return {
        id: img.id,
        filename: img.name || 'unknown',
        dataUrl: img.dataUrl,
        category: img.category || 'kits',
        uploadDate: new Date().toISOString()
      }
    })

    // Merge kit images with existing images instead of replacing all
    const existingImages = await publicCacheService.getImages()
    const nonKitImages = (Array.isArray(existingImages) ? existingImages : []).filter(img => img.category !== 'kits')
    const mergedImages = [...nonKitImages, ...publicKitImages]

    // Store images in localStorage (kits are already stored in salty_mermaid_kits)
    try {
      localStorage.setItem('salty_mermaid_public_images', JSON.stringify(mergedImages))
      console.log('AdminProducts: Successfully stored images to localStorage')
    } catch (error) {
      console.error('AdminProducts: Error storing to localStorage:', error)
    }

    console.log('AdminProducts: Public cache updated with', publicKits.length, 'kits and', publicKitImages.length, 'kit images (merged with', nonKitImages.length, 'existing non-kit images)')
  }

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory
    const matchesStock = filterStock === 'all' || 
                        (filterStock === 'in-stock' && product.inStock && product.stockCount > 0) ||
                        (filterStock === 'low-stock' && product.inStock && product.stockCount <= 5) ||
                        (filterStock === 'out-of-stock' && (!product.inStock || product.stockCount === 0))
    const matchesActive = filterActive === 'all' || 
                         (filterActive === 'active' && product.isActive) ||
                         (filterActive === 'inactive' && !product.isActive)
    
    return matchesSearch && matchesCategory && matchesStock && matchesActive
  })

  const totalProducts = products.length
  const activeProducts = products.filter(p => p.isActive).length
  const totalValue = products.reduce((sum, p) => sum + (p.price * p.stockCount), 0)
  const lowStockCount = products.filter(p => p.inStock && p.stockCount <= 5).length

  return (
    <div className="gap-4 d-flex flex-column">
      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-4">
        <div>
          <h2 className="fs-2 fw-bold text-dark">Products & Kits Management</h2>
          <p className="text-muted">
            {activeProducts}/{totalProducts} active | ${totalValue.toFixed(2)} inventory value | {lowStockCount} low stock
          </p>
        </div>
        <button
          onClick={() => {
            setEditingProduct({
              id: 'new',
              name: '',
              description: '',
              price: 0,
              category: 'Sea Glass Kits',
              brand: 'The Salty Mermaid',
              inStock: true,
              stockCount: 0,
              features: [],
              tags: [],
              isActive: true,
              weight: 0,
              dimensions: '',
              sku: ''
            })
            setShowAddForm(true)
          }}
          className="btn btn-primary"
        >
          Add New Product
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded shadow p-4">
        <div className="row g-4">
          <div className="col-md">
            <input
              type="text"
              placeholder="Search products or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          <div className="col-md">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="form-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div className="col-md">
            <select
              value={filterStock}
              onChange={(e) => setFilterStock(e.target.value)}
              className="form-select"
            >
              <option value="all">All Stock</option>
              <option value="in-stock">In Stock</option>
              <option value="low-stock">Low Stock (≤5)</option>
              <option value="out-of-stock">Out of Stock</option>
            </select>
          </div>
          <div className="col-md">
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="form-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
          <div className="col-md-auto text-end">
            <span className="small text-muted">{filteredProducts.length} products</span>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded shadow overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th className="px-3 py-3 text-start small fw-medium text-muted text-uppercase">Product</th>
                <th className="px-3 py-3 text-start small fw-medium text-muted text-uppercase">Category</th>
                <th className="px-3 py-3 text-start small fw-medium text-muted text-uppercase">Price</th>
                <th className="px-3 py-3 text-start small fw-medium text-muted text-uppercase">Stock</th>
                <th className="px-3 py-3 text-start small fw-medium text-muted text-uppercase">SKU</th>
                <th className="px-3 py-3 text-start small fw-medium text-muted text-uppercase">Status</th>
                <th className="px-3 py-3 text-start small fw-medium text-muted text-uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {filteredProducts.map((product) => {
                const productImage = getProductImage(product)
                return (
                  <tr key={product.id} className={!product.isActive ? 'opacity-50' : ''}>
                    <td className="px-3 py-4">
                      <div className="d-flex align-items-center">
                        <div className="bg-light rounded d-flex align-items-center justify-content-center overflow-hidden me-4" style={{height: '4rem', width: '4rem'}}>
                          {productImage?.dataUrl ? (
                            <img 
                              src={productImage.dataUrl} 
                              alt={product.name}
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : (
                            <div className="fs-4">📦</div>
                          )}
                        </div>
                        <div>
                          <div className="small fw-medium text-dark">{product.name}</div>
                          <div className="small text-muted text-truncate">{product.description}</div>
                          <div className="small text-muted">{product.brand}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-nowrap">
                      <span className="badge bg-primary-subtle text-primary rounded-pill">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-nowrap small fw-medium text-dark">
                      ${product.price.toFixed(2)}
                    </td>
                    <td className="px-3 py-4 text-nowrap">
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="number"
                          value={product.stockCount}
                          onChange={(e) => handleStockUpdate(product.id, parseInt(e.target.value) || 0)}
                          className="form-control form-control-sm" style={{width: '4rem'}}
                          min="0"
                        />
                        <span className={`badge rounded-pill ${
                          product.stockCount === 0 
                            ? 'bg-danger-subtle text-danger' 
                            : product.stockCount <= 5 
                            ? 'bg-warning-subtle text-warning' 
                            : 'bg-success-subtle text-success'
                        }`}>
                          {product.stockCount === 0 
                            ? 'Out' 
                            : product.stockCount <= 5 
                            ? 'Low' 
                            : 'In Stock'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-nowrap small text-dark">
                      {product.sku || 'N/A'}
                    </td>
                    <td className="px-3 py-4 text-nowrap">
                      <span className={`badge rounded-pill ${
                        product.isActive ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'
                      }`}>
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-nowrap small fw-medium">
                      <button
                        onClick={() => setEditingProduct(product)}
                        className="btn btn-link btn-sm text-primary p-0 me-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(product.id)}
                        className={product.isActive ? 'btn btn-link btn-sm text-warning p-0 me-2' : 'btn btn-link btn-sm text-success p-0 me-2'}
                      >
                        {product.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="btn btn-link btn-sm text-danger p-0"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-5">
          <div className="display-1 mb-4">📦</div>
          <p className="text-muted fs-5">No products found</p>
          <p className="text-muted small">
            {searchTerm || filterCategory !== 'all' || filterStock !== 'all' || filterActive !== 'all'
              ? 'Try adjusting your search criteria' 
              : 'Add your first product to get started'}
          </p>
        </div>
      )}

      {/* Professional Edit/Add Modal */}
      {(editingProduct || showAddForm) && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{
            zIndex: 999999,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }}
        >
          <div 
            className="bg-white rounded-3 shadow-lg w-100 position-relative"
            style={{
              maxWidth: '1400px',
              maxHeight: '95vh',
              overflowY: 'auto',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
            }}
          >
            {/* Professional Header */}
            <div 
              className="p-4 border-bottom position-sticky top-0 bg-white rounded-top"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderTopLeftRadius: '0.75rem',
                borderTopRightRadius: '0.75rem'
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="h4 fw-bold mb-1 text-white">
                    {editingProduct?.id === 'new' ? '✨ Add New Product Kit' : '🎨 Edit Product Kit'}
                  </h2>
                  <p className="mb-0 opacity-75 small">
                    {editingProduct?.id === 'new' 
                      ? 'Create a new art kit for your customers'
                      : `Editing: ${editingProduct?.name || 'Unnamed Product'}`
                    }
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingProduct(null)
                    setShowAddForm(false)
                  }}
                  className="btn btn-link text-white p-2"
                  style={{ fontSize: '1.5rem', opacity: 0.8 }}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Spacious Content Area */}
            <div className="p-5">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fs-4 fw-bold mb-0">
                  {editingProduct?.id === 'new' ? 'Add New Product' : 'Edit Product'}
                </h3>
                <button
                  onClick={() => {
                    setEditingProduct(null)
                    setShowAddForm(false)
                  }}
                  className="btn-close"
                  aria-label="Close"
                ></button>
              </div>

              {/* Elegant Form Sections */}
              <div className="row g-5">
                {/* Primary Information Section */}
                <div className="col-lg-7">
                  {/* Basic Information Card */}
                  <div 
                    className="card border-0 shadow-sm mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      borderRadius: '1rem'
                    }}
                  >
                    <div className="card-header border-0 pb-0" style={{ background: 'transparent' }}>
                      <h5 className="card-title text-primary fw-bold mb-0">
                        🎨 Product Information
                      </h5>
                      <p className="text-muted small mb-0">Essential details about your art kit</p>
                    </div>
                    <div className="card-body pt-3">
                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark mb-2">
                          Product Name *
                        </label>
                        <input
                          type="text"
                          value={editingProduct?.name || ''}
                          onChange={(e) => editingProduct && setEditingProduct({...editingProduct, name: e.target.value})}
                          className="form-control form-control-lg"
                          style={{
                            borderRadius: '0.75rem',
                            border: '2px solid #e5e7eb',
                            transition: 'all 0.2s ease',
                            fontSize: '1rem'
                          }}
                          placeholder="e.g., Sea Glass Christmas Tree Kit - Small"
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                      </div>

                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark mb-2">
                          Description *
                        </label>
                        <textarea
                          value={editingProduct?.description || ''}
                          onChange={(e) => editingProduct && setEditingProduct({...editingProduct, description: e.target.value})}
                          rows={4}
                          className="form-control"
                          style={{
                            borderRadius: '0.75rem',
                            border: '2px solid #e5e7eb',
                            transition: 'all 0.2s ease',
                            fontSize: '1rem',
                            resize: 'vertical'
                          }}
                          placeholder="Describe what's included in this kit, difficulty level, and what customers will create..."
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                      </div>

                      <div className="row g-4">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark mb-2">
                            💰 Price *
                          </label>
                          <div className="input-group">
                            <span 
                              className="input-group-text bg-light fw-bold"
                              style={{
                                borderRadius: '0.75rem 0 0 0.75rem',
                                border: '2px solid #e5e7eb',
                                borderRight: 'none'
                              }}
                            >
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              value={editingProduct?.price || 0}
                              onChange={(e) => editingProduct && setEditingProduct({...editingProduct, price: parseFloat(e.target.value) || 0})}
                              className="form-control"
                              style={{
                                borderRadius: '0 0.75rem 0.75rem 0',
                                border: '2px solid #e5e7eb',
                                borderLeft: 'none',
                                fontSize: '1rem'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#667eea'
                                e.target.previousElementSibling.style.borderColor = '#667eea'
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb'
                                e.target.previousElementSibling.style.borderColor = '#e5e7eb'
                              }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark mb-2">
                            📦 Stock Quantity *
                          </label>
                          <input
                            type="number"
                            value={editingProduct?.stockCount || 0}
                            onChange={(e) => editingProduct && setEditingProduct({...editingProduct, stockCount: parseInt(e.target.value) || 0, inStock: (parseInt(e.target.value) || 0) > 0})}
                            className="form-control"
                            style={{
                              borderRadius: '0.75rem',
                              border: '2px solid #e5e7eb',
                              fontSize: '1rem'
                            }}
                            min="0"
                            onFocus={(e) => e.target.style.borderColor = '#667eea'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        </div>
                      </div>

                      <div className="row g-4 mb-0">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark mb-2">
                            🎨 Category *
                          </label>
                          <select
                            value={editingProduct?.category || 'Sea Glass Kits'}
                            onChange={(e) => editingProduct && setEditingProduct({...editingProduct, category: e.target.value as any})}
                            className="form-select"
                            style={{
                              borderRadius: '0.75rem',
                              border: '2px solid #e5e7eb',
                              fontSize: '1rem'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#667eea'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          >
                            {categories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold text-dark mb-2">
                            🏢 Brand *
                          </label>
                          <input
                            type="text"
                            value={editingProduct?.brand || ''}
                            onChange={(e) => editingProduct && setEditingProduct({...editingProduct, brand: e.target.value})}
                            className="form-control"
                            style={{
                              borderRadius: '0.75rem',
                              border: '2px solid #e5e7eb',
                              fontSize: '1rem'
                            }}
                            placeholder="The Salty Mermaid"
                            onFocus={(e) => e.target.style.borderColor = '#667eea'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col">
                      <label className="form-label small fw-medium">SKU</label>
                      <input
                        type="text"
                        value={editingProduct?.sku || ''}
                        onChange={(e) => editingProduct && setEditingProduct({...editingProduct, sku: e.target.value})}
                        className="form-control"
                        placeholder="e.g., SG-TREE-SM-001"
                      />
                    </div>
                    <div className="col">
                      <label className="form-label small fw-medium">Weight (lbs)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={editingProduct?.weight || 0}
                        onChange={(e) => editingProduct && setEditingProduct({...editingProduct, weight: parseFloat(e.target.value) || 0})}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="form-label small fw-medium">Dimensions</label>
                    <input
                      type="text"
                      value={editingProduct?.dimensions || ''}
                      onChange={(e) => editingProduct && setEditingProduct({...editingProduct, dimensions: e.target.value})}
                      className="form-control"
                      placeholder="e.g., 12'' x 8'' x 4''"
                    />
                  </div>
                </div>

                {/* Media & Features Section */}
                <div className="col-lg-5">
                  {/* Professional Image Upload Card */}
                  <div 
                    className="card border-0 shadow-sm mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      borderRadius: '1rem'
                    }}
                  >
                    <div className="card-header border-0 pb-0" style={{ background: 'transparent' }}>
                      <h5 className="card-title text-primary fw-bold mb-0">
                        🖼️ Product Image
                      </h5>
                      <p className="text-muted small mb-0">High-quality photo of your art kit</p>
                    </div>
                    <div className="card-body pt-3">
                      <div 
                        className="border border-2 border-dashed rounded-3 p-4 text-center"
                        style={{
                          borderColor: editingProduct?.imageId ? '#10b981' : '#d1d5db',
                          background: editingProduct?.imageId 
                            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                            : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {editingProduct?.imageId ? (
                          <div className="d-flex flex-column align-items-center">
                            <div 
                              className="w-100 rounded-3 d-flex align-items-center justify-content-center mb-3 overflow-hidden"
                              style={{
                                height: '200px',
                                background: '#f8fafc',
                                border: '2px solid #e5e7eb'
                              }}
                            >
                              {kitImages.find(img => img.id === editingProduct.imageId)?.dataUrl ? (
                                <img 
                                  src={kitImages.find(img => img.id === editingProduct.imageId)?.dataUrl} 
                                  alt="Product Preview"
                                  className="h-100 w-100 object-fit-cover"
                                  style={{ borderRadius: '0.75rem' }}
                                />
                              ) : (
                                <div className="text-center text-muted">
                                  <div className="display-4 mb-2">🖼️</div>
                                  <p className="mb-0">Loading image...</p>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => openImagePicker(editingProduct.id)}
                              className="btn btn-outline-primary btn-sm"
                              style={{
                                borderRadius: '0.5rem',
                                fontWeight: '600'
                              }}
                            >
                              🔄 Change Image
                            </button>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center py-4">
                            <div className="text-center mb-3">
                              <div className="display-1 text-muted mb-3" style={{ fontSize: '4rem' }}>🖼️</div>
                              <h6 className="text-dark fw-semibold mb-2">No Image Selected</h6>
                              <p className="text-muted small mb-0">Choose a high-quality photo that showcases your art kit</p>
                            </div>
                            <button
                              onClick={() => openImagePicker(editingProduct?.id || '')}
                              className="btn btn-primary"
                              style={{
                                borderRadius: '0.75rem',
                                padding: '0.75rem 1.5rem',
                                fontWeight: '600'
                              }}
                            >
                              📋 Select Image
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Features & Tags Card */}
                  <div 
                    className="card border-0 shadow-sm mb-4"
                    style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      borderRadius: '1rem'
                    }}
                  >
                    <div className="card-header border-0 pb-0" style={{ background: 'transparent' }}>
                      <h5 className="card-title text-primary fw-bold mb-0">
                        ✨ Features & Tags
                      </h5>
                      <p className="text-muted small mb-0">What's included and searchable keywords</p>
                    </div>
                    <div className="card-body pt-3">
                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark mb-2">
                          🎨 Kit Features
                        </label>
                        <textarea
                          value={(editingProduct?.features && Array.isArray(editingProduct.features) ? editingProduct.features.join('\n') : '') || ''}
                          onChange={(e) => editingProduct && setEditingProduct({
                            ...editingProduct, 
                            features: e.target.value.split('\n').filter(f => f.trim())
                          })}
                          rows={5}
                          className="form-control"
                          style={{
                            borderRadius: '0.75rem',
                            border: '2px solid #e5e7eb',
                            fontSize: '1rem',
                            resize: 'vertical'
                          }}
                          placeholder="• Premium sea glass pieces\n• Step-by-step instruction guide\n• All necessary crafting tools\n• Gift box packaging"
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                        <small className="text-muted">Enter each feature on a new line</small>
                      </div>

                      <div className="mb-0">
                        <label className="form-label fw-semibold text-dark mb-2">
                          🏷️ Search Tags
                        </label>
                        <input
                          type="text"
                          value={(editingProduct?.tags && Array.isArray(editingProduct.tags) ? editingProduct.tags.join(', ') : '') || ''}
                          onChange={(e) => editingProduct && setEditingProduct({
                            ...editingProduct, 
                            tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
                          })}
                          className="form-control"
                          style={{
                            borderRadius: '0.75rem',
                            border: '2px solid #e5e7eb',
                            fontSize: '1rem'
                          }}
                          placeholder="sea-glass, DIY, coastal, beginner, gift"
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        />
                        <small className="text-muted">Separate tags with commas</small>
                      </div>
                    </div>
                  </div>

                  {/* Status Settings Card */}
                  <div 
                    className="card border-0 shadow-sm"
                    style={{
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      borderRadius: '1rem'
                    }}
                  >
                    <div className="card-header border-0 pb-0" style={{ background: 'transparent' }}>
                      <h5 className="card-title text-primary fw-bold mb-0">
                        ⚙️ Status Settings
                      </h5>
                      <p className="text-muted small mb-0">Availability and visibility options</p>
                    </div>
                    <div className="card-body pt-3">
                      <div className="d-flex flex-column gap-3">
                        <div 
                          className="form-check p-3 rounded-3"
                          style={{
                            background: editingProduct?.inStock 
                              ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' 
                              : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                            border: `2px solid ${editingProduct?.inStock ? '#10b981' : '#ef4444'}`
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={editingProduct?.inStock || false}
                            onChange={(e) => editingProduct && setEditingProduct({...editingProduct, inStock: e.target.checked})}
                            className="form-check-input me-3"
                            id="inStockCheck"
                            style={{ transform: 'scale(1.2)' }}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="inStockCheck">
                            {editingProduct?.inStock ? '✅ In Stock' : '❌ Out of Stock'}
                          </label>
                        </div>
                        <div 
                          className="form-check p-3 rounded-3"
                          style={{
                            background: editingProduct?.isActive 
                              ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' 
                              : 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)',
                            border: `2px solid ${editingProduct?.isActive ? '#3b82f6' : '#9ca3af'}`
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={editingProduct?.isActive || false}
                            onChange={(e) => editingProduct && setEditingProduct({...editingProduct, isActive: e.target.checked})}
                            className="form-check-input me-3"
                            id="isActiveCheck"
                            style={{ transform: 'scale(1.2)' }}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="isActiveCheck">
                            {editingProduct?.isActive ? '🔄 Product Active' : '⏸️ Product Inactive'}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Action Buttons */}
              <div 
                className="d-flex gap-3 pt-5 mt-4"
                style={{
                  borderTop: '2px solid #e5e7eb',
                  background: 'transparent'
                }}
              >
                <button
                  onClick={() => editingProduct && handleSaveProduct(editingProduct)}
                  disabled={!editingProduct?.name || !editingProduct?.brand}
                  className="btn btn-primary flex-fill py-3 fw-bold"
                  style={{
                    borderRadius: '0.75rem',
                    fontSize: '1.1rem',
                    background: (!editingProduct?.name || !editingProduct?.brand) 
                      ? '#9ca3af' 
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(0)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!e.currentTarget.disabled) {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 8px 15px -3px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {editingProduct?.id === 'new' ? '✨ Create Product' : '💾 Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setEditingProduct(null)
                    setShowAddForm(false)
                  }}
                  className="btn btn-outline-secondary py-3 fw-semibold"
                  style={{
                    borderRadius: '0.75rem',
                    fontSize: '1rem',
                    borderWidth: '2px',
                    minWidth: '120px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Picker Modal */}
      <SharePointImagePicker
        isOpen={isPickerOpen}
        onClose={closePicker}
        onSelect={handleImageSelect}
        title="Select Product Image"
        allowUpload={true}
      />
    </div>
  )
}

export default AdminProductsManager